import type {
  CommerceLine as CommerceLineId,
  ProductLine as ProductLineId,
  PublishingSite as PublishingSiteId,
  StudioExperience as StudioExperienceId,
  SubdivisionMarket as SubdivisionMarketId,
  SubdivisionTenant as SubdivisionTenantId,
} from '@studio/utils/experience';

export interface ContentPromoterPreference {
  readonly enableNextGenApp: boolean | null;
  readonly hideAdvancedFilters: boolean;
}

export interface TablePreference {
  readonly rowsPerPage: number;
}

export type TimeZonePreference =
  | { readonly type: 'realm' }
  | { readonly type: 'local' }
  | { readonly type: 'custom'; readonly timeZone: string };

export interface RegionPreference {
  readonly region: string;
}

export enum RecentlyViewedEntryType {
  Unknown = 'unknown',
  // Videos
  Video = 'video',
  Show = 'show',
  Channel = 'channel',
  Livestream = 'livestream',
  Airing = 'airing',
  DistributionChannel = 'distributionChannel',
  VirtualChannel = 'virtualChannel',

  // Ratings
  ContentRatingSystem = 'contentRatingSystem',
  ContentRating = 'contentRating',
  ContentDescriptor = 'contentDescriptor',

  // Taxonomy
  TaxonomyNode = 'taxonomyNode',
  TaxonomyKind = 'taxonomyKind',
  TaxonomyRelationshipDefinition = 'taxonomyRelationshipDefinition',

  // Site builder
  Collection = 'collection',
  Page = 'page',
  Article = 'article',
  Avatar = 'avatar',
  Image = 'image',
  Link = 'link',
  RawContent = 'rawContent',
  SiteBuilderExperiment = 'siteBuilderExperiment',
  SiteBuilderFeature = 'siteBuilderFeature',

  // Monetization
  PricePlan = 'pricePlan',
  Plan = 'plan',
  Campaign = 'campaign',
  Product = 'product',
  ProductTransition = 'productTransition',
  Package = 'package',
  Contract = 'contract',
  Affiliate = 'affiliate',
  MarketPrice = 'marketPrice',

  // Users
  User = 'user',

  // Labs
  Experiment = 'experiment',
  FeatureFlag = 'featureFlag',

  // Subscription journey
  MarketingPage = 'marketingPage',
  MarketingJourney = 'marketingJourney',
  MarketingRoute = 'marketingRoute',
  MarketingCollectionGroup = 'marketingCollectionGroup',
  MarketingCollection = 'marketingCollection',
  MarketingExperiment = 'marketingExperiment',

  // Comms
  EmailTemplate = 'emailTemplate',

  // Settings
  Site = 'site',
  PublishingSite = 'publishingSite',
  Brand = 'brand',
  ContentRestriction = 'contentRestriction',
  Territory = 'territory',
  TerritoryGroup = 'territoryGroup',
  ContentSubset = 'contentSubset',
  ProductLine = 'productLine',

  // Studio Access
  StudioUser = 'studioUser',
  Team = 'team',
  PermissionGroup = 'permissionGroup',
  StudioTool = 'studioTool',
  StudioAccessRequest = 'studioAccessRequest',

  // Capabilities
  CapabilityDefinition = 'capabilityDefinition',
  ContentSegment = 'contentSegment',
  CapabilityPackage = 'capabilityPackage',

  // PPV
  PriceLevel = 'priceLevel',
  PriceLevelProposal = 'priceLevelProposal',
  OfferDefinition = 'offerDefinition',
  OfferDefinitionProposal = 'offerDefinitionProposal',
  Offer = 'offer',

  // Global Policies
  GlobalPolicy = 'globalPolicy',
}

export interface RecentlyViewedEntry {
  readonly type: RecentlyViewedEntryType;
  readonly id: string;
  readonly title: string;
  readonly timestamp: string;
  readonly studioExperience?: string;
  readonly path?: string;
}

export interface RecentlyViewed {
  readonly entries: readonly RecentlyViewedEntry[];
}

export enum SavedItemType {
  ContentView = 'contentView',
  Program = 'program',
  Series = 'series',
  Season = 'season',
}

export interface SavedItemView {
  readonly type: SavedItemType.ContentView;
  readonly query: string;
  readonly label: string;
  readonly pinned: boolean;
}

export interface SavedItemResource {
  readonly type:
    | SavedItemType.Program
    | SavedItemType.Series
    | SavedItemType.Season;
  readonly id: string;
  readonly label: string;
  readonly pinned: boolean;
}

export type SavedItem = SavedItemView | SavedItemResource;

export interface SavedItems {
  readonly savedItems: readonly SavedItem[];
}

export interface InfoPanelPreference {
  readonly collapsed: boolean;
}

export interface CmsBookmarksV1 {
  readonly version: 1;
  readonly bookmarks: Readonly<
    Record<string, Readonly<Record<string, boolean>>>
  >;
}

export interface CmsBookmarkV2 {
  readonly id: string;
  readonly type: string;
  readonly publishingSiteId: PublishingSiteId | null;
}

export interface CmsBookmarksV2 {
  readonly version: 2;
  readonly bookmarks: readonly CmsBookmarkV2[];
}

export type CmsBookmarks = CmsBookmarksV1 | CmsBookmarksV2;

export interface Bookmark {
  readonly type: string;
  readonly studioExperience?: StudioExperienceId | null;
  readonly subdivisionTenant?: SubdivisionTenantId | null;
  readonly subdivisionMarket?: SubdivisionMarketId | null;
  readonly commerceLine?: CommerceLineId | null;
  readonly productLine?: ProductLineId | null;
  readonly publishingSite?: PublishingSiteId | null;
  readonly route: string;
  readonly title?: string;
  readonly isObjectLink: boolean;
  readonly appKey: string;
}
export interface Bookmarks {
  readonly version: 1;
  readonly bookmarks: readonly Bookmark[];
}

export type BookmarksSortType = 'application' | 'link';

export interface BookmarksSortPreference {
  readonly sortBy: BookmarksSortType;
}

export interface ResourceIdentifier {
  readonly id: string;
  readonly type: string;
}

export interface CmsEditorHistory {
  readonly version: 1;
  readonly recentlyOpened: readonly ResourceIdentifier[];
}

export interface InitialPublishingSite {
  readonly publishingSiteId: PublishingSiteId;
}

export interface InitialProductLine {
  readonly productLineId: string;
}

export interface InitialContentSubset {
  readonly contentSubsetId: string;
}

export interface MultiverseStorage {
  readonly autoEnroll: string[];
  readonly allowList: string[];
  readonly releaseEnroll: string[];
}

export interface TableStorageTable {
  columnVisibilityModel?: Record<string, boolean>;
}

export interface TableStorage {
  readonly tables: Readonly<Record<string, Readonly<TableStorageTable>>>;
}

export interface StudioQuery {
  id: string;
  timestamp: number;
}

export interface StudioQueryStorage {
  readonly queries: StudioQuery[];
}

export interface PersonalSettingsStorage {
  readonly backgroundVideoToggle: boolean;
}
